import React, { useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, Loader } from "lucide-react";
import StepChoosePaidEmployees from "./StepChoosePaidEmployees";
import StepReviewUnpay from "./StepReviewUnpay";

const BulkUnpayModal = ({
  isOpen,
  onClose,
  payrollData = [],
  currentTheme,
  t,
  onUnpayComplete,
  showAlert,
  apiFetch,
  API_BASE_URL,
}) => {
  const [step, setStep] = useState(1);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleEmployeeSelection = useCallback((employee) => {
    setSelectedEmployees((prev) => {
      const isSelected = prev.some((emp) => emp.id === employee.id);
      if (isSelected) {
        return prev.filter((emp) => emp.id !== employee.id);
      } else {
        return [...prev, employee];
      }
    });
  }, []);

  const handleNext = () => {
    if (step === 1 && selectedEmployees.length === 0) {
      if (showAlert) {
        showAlert(t("error"), t("selectAtLeastOneEmployee"), "error");
      }
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleUnpayAll = async () => {
    if (selectedEmployees.length === 0) {
      if (showAlert) {
        showAlert(t("error"), t("selectAtLeastOneEmployee"), "error");
      }
      return;
    }

    setIsProcessing(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const employee of selectedEmployees) {
        try {
          const response = await apiFetch(
            `${API_BASE_URL}/payrollX/${employee.id}/unpay`,
            {
              method: "PATCH",
            }
          );

          const data = await response.json();
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            errors.push(`${employee.employeeName}: ${data.error}`);
          }
        } catch (err) {
          errorCount++;
          errors.push(`${employee.employeeName}: ${err.message}`);
        }
      }

      if (onUnpayComplete) {
        onUnpayComplete();
      }

      if (errorCount === 0) {
        if (showAlert) {
          showAlert(
            t("success"),
            t("unpayAllSuccess").replace("{count}", successCount),
            "success"
          );
        }
      } else if (successCount > 0) {
        if (showAlert) {
          showAlert(
            t("warning"),
            t("unpayPartialSuccess")
              .replace("{success}", successCount)
              .replace("{error}", errorCount),
            "warning"
          );
        }
      } else {
        if (showAlert) {
          showAlert(
            t("error"),
            t("unpayAllFailed") + "\n" + errors.join("\n"),
            "error"
          );
        }
      }

      // Reset and close
      setStep(1);
      setSelectedEmployees([]);
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedEmployees([]);
    onClose();
  };

  if (!isOpen) return null;

  const modalStyles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    modal: {
      background: currentTheme.colors.cardBackground,
      borderRadius: "16px",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
      maxWidth: "600px",
      width: "90%",
      maxHeight: "80vh",
      display: "flex",
      flexDirection: "column",
      border: `1px solid ${currentTheme.colors.borderLight}`,
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "24px",
      borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
    },
    title: {
      fontSize: "18px",
      fontWeight: "700",
      color: currentTheme.colors.textPrimary,
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    content: {
      flex: 1,
      overflowY: "auto",
      padding: "24px 0",
    },
    footer: {
      display: "flex",
      justifyContent: "space-between",
      gap: "12px",
      padding: "24px",
      borderTop: `1px solid ${currentTheme.colors.borderLight}`,
    },
    button: {
      base: {
        padding: "10px 20px",
        borderRadius: "8px",
        border: "none",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      },
      primary: {
        background: currentTheme.colors.danger,
        color: "white",
      },
      secondary: {
        background: currentTheme.colors.borderLight,
        color: currentTheme.colors.textPrimary,
      },
    },
  };

  const stepTitles = [
    { title: t("choosePaidEmployees"), icon: AlertTriangle },
    { title: t("reviewUnpay"), icon: CheckCircle },
  ];

  const currentStepInfo = stepTitles[step - 1];
  const StepIcon = currentStepInfo.icon;

  return (
    <div style={modalStyles.overlay} onClick={handleClose}>
      <div
        style={modalStyles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={modalStyles.header}>
          <div style={modalStyles.title}>
            <StepIcon size={20} color={currentTheme.colors.danger} />
            {currentStepInfo.title}
          </div>
          <button
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: currentTheme.colors.textSecondary,
              fontSize: "24px",
              padding: 0,
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Step Indicator */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            padding: "0 24px",
            marginBottom: "16px",
            justifyContent: "center",
          }}
        >
          {stepTitles.map((_, index) => (
            <div
              key={index}
              style={{
                height: "4px",
                flex: 1,
                background:
                  index + 1 <= step
                    ? currentTheme.colors.danger
                    : currentTheme.colors.borderLight,
                borderRadius: "2px",
                transition: "all 0.3s",
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div style={modalStyles.content}>
          {step === 1 && (
            <StepChoosePaidEmployees
              payrollData={payrollData}
              selectedEmployees={selectedEmployees}
              setSelectedEmployees={setSelectedEmployees}
              handleEmployeeSelection={handleEmployeeSelection}
              setSelectedAll={setSelectedAll}
              modalStyles={{
                employeeItem: {
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                },
              }}
              currentTheme={currentTheme}
              t={t}
            />
          )}
          {step === 2 && (
            <StepReviewUnpay
              selectedEmployees={selectedEmployees}
              modalStyles={{
                employeeItem: {
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                },
              }}
              currentTheme={currentTheme}
              t={t}
            />
          )}
        </div>

        {/* Footer */}
        <div style={modalStyles.footer}>
          <button
            onClick={handleBack}
            disabled={step === 1}
            style={{
              ...modalStyles.button.base,
              ...modalStyles.button.secondary,
              opacity: step === 1 ? 0.5 : 1,
              cursor: step === 1 ? "not-allowed" : "pointer",
            }}
          >
            <ChevronLeft size={16} />
            {t("previous")}
          </button>

          {step === 1 && (
            <button
              onClick={handleNext}
              disabled={selectedEmployees.length === 0}
              style={{
                ...modalStyles.button.base,
                ...modalStyles.button.primary,
                opacity: selectedEmployees.length === 0 ? 0.5 : 1,
                cursor:
                  selectedEmployees.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              {t("nextStep")}
              <ChevronRight size={16} />
            </button>
          )}

          {step === 2 && (
            <button
              onClick={handleUnpayAll}
              disabled={isProcessing}
              style={{
                ...modalStyles.button.base,
                ...modalStyles.button.primary,
                opacity: isProcessing ? 0.7 : 1,
                cursor: isProcessing ? "not-allowed" : "pointer",
              }}
            >
              {isProcessing ? (
                <>
                  <Loader size={16} style={{ animation: "spin 1s linear infinite" }} />
                  {t("processing")}
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  {t("confirmUnpayAll")}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BulkUnpayModal;
